import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/contexts/ProjectContext";
import { LogOut, Menu, User, Bell, ChevronDown, FolderOpen, Building } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";

export default function NavBar() {
  const { user, logout } = useAuth();
  const { currentProject, projects, setCurrentProject } = useProject();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleProjectSwitch = (project: any) => {
    setCurrentProject(project);
  };

  return (
    <div className="border-b">
      <div className="flex h-16 items-center px-4 md:px-6">
        <Link to="/dashboard" className="mr-4 flex items-center">
          <span className="font-bold text-xl text-primary mr-1">Strata</span>
          <span className="font-medium text-xl text-secondary">fold</span>
        </Link>
        
        {/* Current Project Display */}
        {currentProject && (
          <div className="hidden md:flex items-center mx-4 px-3 py-1 bg-muted rounded-md">
            <Building className="h-4 w-4 mr-2 text-muted-foreground" />
            <span className="text-sm font-medium">{currentProject.name}</span>
          </div>
        )}
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium flex-1">
          <Link
            to="/projects-selector"
            className="transition-colors hover:text-primary flex items-center gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Portfolio
          </Link>
          
          {currentProject && (
            <>
              <Link
                to="/dashboard"
                className="transition-colors hover:text-primary"
              >
                Dashboard
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-sm font-medium hover:text-primary">
                    Business Case Tools
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem asChild>
                    <Link to="/ask">Ask Casey (AI Assistant)</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/knowledge-centre">Knowledge Centre</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/training-hub">Training Hub</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/business-case-developer">Business Case Developer</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-sm font-medium hover:text-primary">
                    Consultancy Tools
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem asChild>
                    <Link to="/options-appraisal">Options Appraisal</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/swot-analysis">SWOT Analysis</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/qualitative-assessment">Qualitative Assessment</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/economic-assessment">Economic Assessment</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/financial-assessment">Financial Assessment</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/minutes-tracker">Minutes & Action Tracker</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-sm font-medium hover:text-primary">
                    Performance
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem asChild>
                    <Link to="/progress-reporting">Progress Reporting</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/compliance-tracking">Compliance Tracking</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/cost-tracking">Cost Tracking</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/risk-tracker">Risk Tracker</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/sustainability-tracker">Sustainability Tracker</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link
                to="/capital-programme"
                className="transition-colors hover:text-primary"
              >
                Capital Programme
              </Link>
            </>
          )}
        </nav>
        
        <div className="ml-auto flex items-center gap-2">
          {/* Project Switcher */}
          {currentProject && projects.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-sm">
                  Switch Project
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Switch Project</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {projects.map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => handleProjectSwitch(project)}
                    className={project.id === currentProject.id ? "bg-muted" : ""}
                  >
                    <Building className="mr-2 h-4 w-4" />
                    {project.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/projects-selector">
                    <FolderOpen className="mr-2 h-4 w-4" />
                    View All Projects
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label="Notifications"
            onClick={() => console.log("Notifications clicked")}
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"></span>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="User menu"
              >
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{user?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {user?.email}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile">Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings">Settings</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => logout()}>
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <span className="font-bold text-xl text-primary mr-1">Strata</span>
                  <span className="font-medium text-xl text-secondary">fold</span>
                </SheetTitle>
              </SheetHeader>
              
              <ScrollArea className="h-[calc(100vh-120px)] mt-8">
                <nav className="flex flex-col gap-4">
                  {/* Current Project Display */}
                  {currentProject && (
                    <div className="flex items-center p-3 bg-muted rounded-lg mb-4">
                      <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm font-medium">{currentProject.name}</span>
                    </div>
                  )}

                  <Link
                    to="/projects-selector"
                    className="flex items-center gap-2 text-lg font-medium py-2 hover:text-primary"
                    onClick={closeMobileMenu}
                  >
                    <FolderOpen className="h-5 w-5" />
                    Portfolio
                  </Link>
                  
                  {currentProject && (
                    <>
                      <Link
                        to="/dashboard"
                        className="text-lg font-medium py-2 hover:text-primary"
                        onClick={closeMobileMenu}
                      >
                        Dashboard
                      </Link>
                      
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Business Case Tools
                        </div>
                        <div className="pl-4 space-y-2">
                          <Link
                            to="/ask"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Ask Casey (AI Assistant)
                          </Link>
                          <Link
                            to="/knowledge-centre"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Knowledge Centre
                          </Link>
                          <Link
                            to="/training-hub"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Training Hub
                          </Link>
                          <Link
                            to="/business-case-developer"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Business Case Developer
                          </Link>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Consultancy Tools
                        </div>
                        <div className="pl-4 space-y-2">
                          <Link
                            to="/options-appraisal"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Options Appraisal
                          </Link>
                          <Link
                            to="/swot-analysis"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            SWOT Analysis
                          </Link>
                          <Link
                            to="/qualitative-assessment"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Qualitative Assessment
                          </Link>
                          <Link
                            to="/economic-assessment"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Economic Assessment
                          </Link>
                          <Link
                            to="/financial-assessment"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Financial Assessment
                          </Link>
                          <Link
                            to="/minutes-tracker"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Minutes & Action Tracker
                          </Link>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                          Performance
                        </div>
                        <div className="pl-4 space-y-2">
                          <Link
                            to="/progress-reporting"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Progress Reporting
                          </Link>
                          <Link
                            to="/compliance-tracking"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Compliance Tracking
                          </Link>
                          <Link
                            to="/cost-tracking"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Cost Tracking
                          </Link>
                          <Link
                            to="/risk-tracker"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Risk Tracker
                          </Link>
                          <Link
                            to="/sustainability-tracker"
                            className="block py-1 hover:text-primary"
                            onClick={closeMobileMenu}
                          >
                            Sustainability Tracker
                          </Link>
                        </div>
                      </div>
                      
                      <Link
                        to="/capital-programme"
                        className="text-lg font-medium py-2 hover:text-primary"
                        onClick={closeMobileMenu}
                      >
                        Capital Programme
                      </Link>
                    </>
                  )}
                </nav>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </div>
  );
}
